/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/

import { SandboxTile, SandboxTileProps, SandboxTileSkeleton } from "components/elements";
import { LoadingSpinner } from "components/loading-spinner/LoadingSpinner";
import { FunctionComponent, useMemo } from "react";
import { Link } from "react-router-dom";
import { routeTo } from "router/routerNavigate";
import { sandboxApi } from "services/sandbox-api/api";
import { CodeShareMetadata } from "types/codeshareTypes";
import { CodeShareId } from "../../services/share-service/types/CodeShareData";
import { BadgeProps, FeaturedBadge, TemplateBadge } from "../badges/Badges";
import { SandboxTilesEmpty } from "./SandboxTilesEmpty";

export interface LoadingSandboxListProps extends Omit<SandboxTileProps, "sandbox" | "active" | "badges"> {
  isLoading: boolean;
  list?: CodeShareMetadata[];
  active?: CodeShareId;
  emptyMessage?: string;
  sort?: (a: CodeShareMetadata, b: CodeShareMetadata) => number;
  rotateBy?: number;
}

export const SandboxTiles: FunctionComponent<LoadingSandboxListProps> = ({ list, isLoading, active, emptyMessage, sort, rotateBy = 0, onClick, ...props }) => {
  const { data: featured } = sandboxApi.useCodeSharesFeaturedQuery(undefined, { selectFromResult: (data) => data });
  const { data: templates } = sandboxApi.useCodeSharesTemplatesQuery(undefined, { selectFromResult: (data) => data });

  const sandboxes = useMemo(() => {
    if (!list || !list.length) {
      return undefined;
    }

    // return rotate(list.sort(sort), rotateBy).map((sandbox: CodeShareMetadata, index) => {
    const transformedList = [...list];

    if (sort)
      transformedList.sort(sort);

    return transformedList.map((sandbox: CodeShareMetadata, index) => {

      if (!(featured && templates))
        return <SandboxTileSkeleton key={`placeholder-${index}`} />;

      const badges: BadgeProps[] = [];

      if (featured.find((f) => f.id === sandbox.id && f.userId === sandbox.userId)) {
        badges.push(FeaturedBadge);
      }

      if (templates.find((t) => t.id === sandbox.id && t.userId === sandbox.userId)) {
        badges.push(TemplateBadge);
      }

      const card = <SandboxTile
        key={`sandbox-card-${index}`}
        sandbox={sandbox}
        isSelected={active?.id === sandbox.id && active?.userId === sandbox.userId}
        badges={badges}
        onClick={onClick}
        {...props}
      />;

      return onClick ? card : <Link key={`sandbox-card-${index}`} style={{ textDecoration: "none" }} to={routeTo.sandbox({ userName: sandbox.username, sandboxName: sandbox.name })}>{card}</Link>;
    });
  }, [list, sort, featured, templates, active, props]);

  return (
    <>
      {!isLoading && sandboxes}
      {!isLoading && sandboxes?.length === 0 && emptyMessage && <SandboxTilesEmpty message={emptyMessage} />}
      {isLoading && <LoadingSpinner centered hPos="center" />}
    </>
  );
};
